import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { AlertTriangle, RefreshCw, CheckCircle } from "lucide-react";
import { generateClient } from "aws-amplify/api";
import {
  listPlayerConstructs,
  listEventPlayerResults,
  listEventStandings,
  listMatches,
  listEventParticipants,
} from "../../graphql/queries";
import {
  updatePlayerConstruct,
  deleteEventPlayerResult,
  deleteEventStanding,
  deleteMatch,
  deleteEventParticipant,
} from "../../graphql/mutations";
import { useQueryClient } from "@tanstack/react-query";

const client = generateClient();

const ResetPoints: React.FC = () => {
  const [isResetting, setIsResetting] = useState(false);
  const [status, setStatus] = useState<string>("");
  const [resetComplete, setResetComplete] = useState(false);
  const queryClient = useQueryClient();

  const resetAllPoints = async () => {
    if (
      !window.confirm(
        "⚠️ This will reset ALL player points and delete ALL event results. Are you sure you want to continue?"
      )
    ) {
      return;
    }

    setIsResetting(true);
    setStatus("🔄 Starting points reset...");

    try {
      // Step 1: Get all player constructs
      setStatus("📋 Fetching all player constructs...");
      const constructsResult = await client.graphql({
        query: listPlayerConstructs,
        variables: { limit: 1000 },
      });

      const constructs = constructsResult.data.listPlayerConstructs.items;
      setStatus(`Found ${constructs.length} player constructs`);

      // Step 2: Reset all player constructs to zero
      setStatus("🔄 Resetting player constructs...");
      for (const construct of constructs) {
        await client.graphql({
          query: updatePlayerConstruct,
          variables: {
            input: {
              id: construct.id,
              totalEvents: 0,
              totalPoints: 0,
              totalWins: 0,
              totalLosses: 0,
              totalDraws: 0,
              bestRank: null,
              averageRank: null,
            },
          },
        });
        setStatus(`✅ Reset ${construct.name}`);
      }

      // Step 3: Get all event player results
      setStatus("📋 Fetching all event player results...");
      const resultsResult = await client.graphql({
        query: listEventPlayerResults,
        variables: { limit: 1000 },
      });

      const results = resultsResult.data.listEventPlayerResults.items;
      setStatus(`Found ${results.length} event player results`);

      // Step 4: Delete all event player results
      setStatus("🗑️ Deleting all event player results...");
      for (const result of results) {
        await client.graphql({
          query: deleteEventPlayerResult,
          variables: {
            input: {
              id: result.id,
            },
          },
        });
        setStatus(
          `✅ Deleted result for ${result.playerConstructId || "Unknown"}`
        );
      }

      // Step 5: Get all event standings (old system)
      setStatus("📋 Fetching all event standings...");
      const standingsResult = await client.graphql({
        query: listEventStandings,
        variables: { limit: 1000 },
      });

      const standings = standingsResult.data.listEventStandings.items;
      setStatus(`Found ${standings.length} event standings`);

      // Step 6: Delete all event standings
      setStatus("🗑️ Deleting all event standings...");
      for (const standing of standings) {
        await client.graphql({
          query: deleteEventStanding,
          variables: {
            input: {
              id: standing.id,
            },
          },
        });
        setStatus(`✅ Deleted standing for ${standing.userId || "Unknown"}`);
      }

      // Step 7: Get all matches
      setStatus("📋 Fetching all matches...");
      const matchesResult = await client.graphql({
        query: listMatches,
        variables: { limit: 1000 },
      });

      const matches = matchesResult.data.listMatches.items;
      setStatus(`Found ${matches.length} matches`);

      // Step 8: Delete all matches
      setStatus("🗑️ Deleting all matches...");
      for (const match of matches) {
        await client.graphql({
          query: deleteMatch,
          variables: {
            input: {
              id: match.id,
            },
          },
        });
        setStatus(`✅ Deleted match ${match.player1Id} vs ${match.player2Id}`);
      }

      // Step 9: Get all event participants
      setStatus("📋 Fetching all event participants...");
      const participantsResult = await client.graphql({
        query: listEventParticipants,
        variables: { limit: 1000 },
      });

      const participants = participantsResult.data.listEventParticipants.items;
      setStatus(`Found ${participants.length} event participants`);

      // Step 10: Delete all event participants
      setStatus("🗑️ Deleting all event participants...");
      for (const participant of participants) {
        await client.graphql({
          query: deleteEventParticipant,
          variables: {
            input: {
              id: participant.id,
            },
          },
        });
        setStatus(`✅ Deleted participant ${participant.userId || "Unknown"}`);
      }

      // Step 11: Invalidate React Query cache
      setStatus("🔄 Clearing cache...");
      await queryClient.invalidateQueries();

      setStatus("🎉 Points reset completed successfully!");
      setResetComplete(true);
    } catch (error) {
      console.error("❌ Error resetting points:", error);
      setStatus(
        `❌ Error: ${error instanceof Error ? error.message : "Unknown error"}`
      );
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Reset All Points</h1>
        <p className="text-muted-foreground">
          Reset all player constructs and delete all event results for testing
        </p>
      </div>

      {/* Warning Card */}
      <Card className="border-red-200 bg-red-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-800">
            <AlertTriangle className="h-5 w-5" />
            ⚠️ DANGEROUS OPERATION
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-red-700">
            <p className="font-medium">This will:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                Reset ALL player constructs to 0 points, 0 events, 0-0-0 record
              </li>
              <li>Delete ALL event player results</li>
              <li>Delete ALL event standings (old system)</li>
              <li>Delete ALL matches</li>
              <li>Delete ALL event participants</li>
              <li>Clear ALL React Query cache</li>
              <li>Force page refresh</li>
              <li>This action cannot be undone!</li>
            </ul>
            <p className="font-medium mt-4">
              Only use this for testing the new import system!
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Reset Button */}
      <Card>
        <CardHeader>
          <CardTitle>Reset Points</CardTitle>
          <CardDescription>
            Click the button below to reset all points and start fresh
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            onClick={resetAllPoints}
            disabled={isResetting}
            variant="destructive"
            size="lg"
            className="w-full"
          >
            {isResetting ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Resetting...
              </>
            ) : (
              <>
                <AlertTriangle className="h-4 w-4 mr-2" />
                Reset All Points
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Status */}
      {status && (
        <Card
          className={
            resetComplete
              ? "border-green-200 bg-green-50"
              : "border-blue-200 bg-blue-50"
          }
        >
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              {resetComplete ? (
                <CheckCircle className="h-4 w-4 text-green-500" />
              ) : (
                <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />
              )}
              <p className="text-sm">{status}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Success Message */}
      {resetComplete && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <h3 className="font-semibold text-green-800">
                  Reset Complete!
                </h3>
              </div>
              <p className="text-sm text-green-700">
                All points have been reset to zero. You can now start importing
                your EventLink results fresh!
              </p>
              <div className="mt-4">
                <p className="text-sm font-medium text-green-800">
                  Next steps:
                </p>
                <ol className="list-decimal list-inside text-sm text-green-700 mt-2 space-y-1">
                  <li>Go to an event and click "Import Results"</li>
                  <li>Paste your EventLink data</li>
                  <li>Test the new player construct system</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ResetPoints;
